/**
 * Shared weekday labels and maps for calendar / availability UIs (Mon-first vs Sun-first).
 */

import type { WeekDay } from "@/domain/core";

/** `date-fns` `getDay()` index: 0 = Sunday … 6 = Saturday */
export const WEEKDAY_TO_JS_GET_DAY_INDEX: Record<WeekDay, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6
};

/** Calendar headers when the week starts on Sunday (US-style month grids). */
export const WEEKDAY_SHORT_LABELS_SUN_FIRST = [
  "Sun",
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat"
] as const;

/** Short labels Mon–Sun (ISO / business-week first column). */
export const WEEKDAY_SHORT_LABELS_MON_FIRST = [
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
  "Sun"
] as const;

export type WeekdayShortLabelMonFirst =
  (typeof WEEKDAY_SHORT_LABELS_MON_FIRST)[number];

export const SHORT_WEEKDAY_LABEL_TO_WEEKDAY: Record<
  WeekdayShortLabelMonFirst,
  WeekDay
> = {
  Mon: "monday",
  Tue: "tuesday",
  Wed: "wednesday",
  Thu: "thursday",
  Fri: "friday",
  Sat: "saturday",
  Sun: "sunday"
};

/** Mon-first columns with weekend flag for muted styling. */
export const WEEKDAY_GRID_COLUMNS_MON_FIRST: ReadonlyArray<{
  day: WeekdayShortLabelMonFirst;
  isWeekend: boolean;
}> = [
    { day: "Mon", isWeekend: false },
    { day: "Tue", isWeekend: false },
    { day: "Wed", isWeekend: false },
    { day: "Thu", isWeekend: false },
    { day: "Fri", isWeekend: false },
    { day: "Sat", isWeekend: true },
    { day: "Sun", isWeekend: true }
  ];

export const WEEKDAY_OPTIONS: ReadonlyArray<{
  label: string;
  short: WeekdayShortLabelMonFirst;
  value: WeekDay;
}> = [
  { label: "Monday", short: "Mon", value: "monday" },
  { label: "Tuesday", short: "Tue", value: "tuesday" },
  { label: "Wednesday", short: "Wed", value: "wednesday" },
  { label: "Thursday", short: "Thu", value: "thursday" },
  { label: "Friday", short: "Fri", value: "friday" },
  { label: "Saturday", short: "Sat", value: "saturday" },
  { label: "Sunday", short: "Sun", value: "sunday" }
];

export const BUSINESS_WEEKDAYS: readonly WeekDay[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday"
];

export const WEEKEND_DAYS: readonly WeekDay[] = ["saturday", "sunday"];

export const ALL_WEEKDAYS_MON_FIRST: readonly WeekDay[] = [
  ...BUSINESS_WEEKDAYS,
  ...WEEKEND_DAYS
];
